import { IUser } from '@/types';

// ================================
// Types
// ================================

/**
 * Supported output formats for `getUserName`.
 */
type TUserNameFormat = 'firstName' | 'lastName' | 'fullName' | 'lastFirst';

/**
 * Parses and formats a user display name into several commonly used variants.
 *
 * This utility supports the two primary source formats seen in user profiles:
 * - Comma-based format: `LastName, FirstName MiddleName`
 * - Space-based format: `FirstName LastName`
 *
 * It also trims optional trailing square-bracket annotations often appended by
 * identity systems (for example: `[JANUS NON-J&J]`). Only trailing bracket
 * blocks are removed; bracket content in the middle of the string is preserved.
 *
 * ## Behavior notes (intentionally preserved)
 * - If `user` or `user.name` is missing, returns an empty string.
 * - If `format` is omitted, returns the original `user.name` unchanged.
 * - `fullName` returns `"{firstName} {lastName}"` based on parsed values.
 *   For a single-token name this yields a trailing space (e.g. `"John "`).
 * - `lastFirst` returns `"LastName, FirstName"` when both are available;
 *   otherwise falls back to whichever part exists.
 *
 * @param user - The user object containing the name property
 * @param format - The format to return ('firstName' | 'lastName' | 'fullName' | 'lastFirst')
 * @returns The formatted name, or an empty string if the name cannot be parsed
 *
 * @example
 * ```ts
 * getUserName({ name: "Smith, John Michael" }, "firstName") // "John"
 * getUserName({ name: "Smith, John Michael [JANUS NON-J&J]" }, "firstName") // "John"
 * getUserName({ name: "Smith, John Michael" }, "lastName") // "Smith"
 * getUserName({ name: "Smith, John Michael [JANUS NON-J&J]" }, "lastName") // "Smith"
 * getUserName({ name: "Smith, John Michael" }, "fullName") // "John Smith"
 * getUserName({ name: "Smith, John [JANUS NON-J&J]" }, "fullName") // "John Smith"
 * getUserName({ name: "Smith, John Michael" }, "lastFirst") // "Smith, John"
 * getUserName({ name: "Smith, John Michael [JANUS NON-J&J]" }, "lastFirst") // "Smith, John"
 * getUserName({ name: "John Smith" }, "firstName") // "John"
 * getUserName({ name: "John Smith" }, "lastName") // "Smith"
 * getUserName({ name: "John Smith" }, "fullName") // "John Smith"
 * getUserName({ name: "John Smith" }, "lastFirst") // "Smith, John"
 * getUserName(null, "firstName") // ""
 * getUserName({ name: null }, "firstName") // ""
 * ```
 */
export function getUserName(user?: IUser | null, format?: TUserNameFormat): string {
  // Step 1: Guard missing user/name input.
  // If no usable name exists, every output format should be an empty string.
  if (!user?.name) return '';

  // Step 2: Preserve original behavior when format is omitted.
  // Return the raw name exactly as provided (including any bracket suffix).
  if (!format) {
    return user.name;
  }

  // Step 3: Normalize trailing metadata.
  // Removes one or more trailing bracketed segments and surrounding whitespace,
  // e.g. "Doe, Jane [TEAM] [DEPT]" -> "Doe, Jane".
  const cleanedName = user.name.replace(/\s*\[.*?\]\s*$/g, '').trim();

  // If cleaning removed everything (e.g. name was only "[DEPT]"), return ''.
  if (!cleanedName) return '';

  // Step 4: Parse name parts.
  // `fullName` here refers to the cleaned input (not output format).
  const fullName = cleanedName;
  let firstName = '';
  let lastName = '';

  // Branch A: Comma-separated source format -> "Last, First Middle"
  if (fullName.includes(',')) {
    const parts = fullName.split(',');
    lastName = parts[0]?.trim() || '';
    const firstNamePart = parts[1]?.trim();
    firstName = firstNamePart?.split(' ')[0] || '';
  } else {
    // Branch B: Space-separated source format -> "First Last"
    const parts = fullName.split(' ');
    firstName = parts[0] || '';
    lastName = parts[parts.length - 1] || '';

    // Single token input (e.g. "John") is first name only.
    if (parts.length === 1) {
      lastName = '';
    }
  }

  // Step 5: Build requested output variant.
  switch (format) {
    case 'firstName':
      return firstName;

    case 'lastName':
      return lastName;

    case 'fullName':
      // Intentionally preserve the existing output shape.
      // For single-token names this returns a trailing space, e.g. "John ".
      return `${firstName} ${lastName}` || cleanedName;

    case 'lastFirst':
      // Prefer canonical "Last, First" when both parts are available.
      if (lastName && firstName) return `${lastName}, ${firstName}`;
      // Fallback order is preserved: firstName -> lastName -> cleanedName.
      return firstName || lastName || cleanedName;

    default:
      // Defensive default: if an unsupported format somehow arrives at runtime,
      // return the original unmodified name string.
      return user.name;
  }
}
